/**
 * WernerArt — Animation Engine
 * Scroll-Reveal via IntersectionObserver + Stagger
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const STAGGER_MS = 90;

  // ── Stagger: expand [data-wa-stagger] children ─────────────────────────────
  // Reads custom delay from data-wa-stagger-delay (ms), falls back to STAGGER_MS.
  function initStagger() {
    document.querySelectorAll('[data-wa-stagger]').forEach(function (parent) {
      const delay = parseInt(parent.dataset.waStaggerDelay || STAGGER_MS, 10);
      Array.from(parent.children).forEach(function (child, i) {
        if (!child.hasAttribute('data-wa-reveal')) {
          child.setAttribute('data-wa-reveal', '');
        }
        child.style.setProperty('--wa-reveal-delay', i * delay + 'ms');
      });
    });
  }

  // ── Reveal: IntersectionObserver on [data-wa-reveal] ──────────────────────
  function initReveal() {
    const elements = Array.from(document.querySelectorAll('[data-wa-reveal]'));
    if (!elements.length) return;

    if (REDUCED) {
      elements.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -48px 0px'
    });

    elements.forEach(function (el) { observer.observe(el); });
  }

  // ── Hero: add .wa-has-loaded on body after first paint ────────────────────
  // Triggers CSS transitions that depend on page-load state.
  function initPageLoad() {
    // Small timeout so CSS initial states have rendered before adding loaded class
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.body.classList.add('wa-page-loaded');
      });
    });
  }

  // ── Card image parallax-hint on scroll ────────────────────────────────────
  // Very subtle: each card image translates ±4px as you scroll past it.
  // Disabled on reduced-motion and on low-end devices (via connection check).
  function initCardDepth() {
    if (REDUCED) return;
    // Skip on slow connections
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && (conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g')) return;

    const cards = Array.from(document.querySelectorAll('.card-wrapper.product-card-wrapper'));
    if (!cards.length) return;

    let ticking = false;

    function updateCards() {
      const wh = window.innerHeight;
      cards.forEach(function (card) {
        const img = card.querySelector('.card__media img');
        if (!img) return;
        const rect = card.getBoundingClientRect();
        // Progress: 0 when card enters from bottom, 1 when it exits at top
        const progress = (wh - rect.top) / (wh + rect.height);
        const clamped = Math.max(0, Math.min(1, progress));
        // Translate between -4px (entering) and +4px (leaving)
        const shift = (clamped - 0.5) * 8;
        img.style.transform = 'translateY(' + shift.toFixed(2) + 'px)';
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateCards);
        ticking = true;
      }
    }, { passive: true });

    updateCards();
  }

  // ── Nav: shrink header on scroll ──────────────────────────────────────────
  function initHeaderScroll() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    let lastY = 0;
    let ticking = false;

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          const y = window.scrollY;
          if (y > 60) {
            header.classList.add('wa-header-scrolled');
          } else {
            header.classList.remove('wa-header-scrolled');
          }
          // Hide on scroll down, show on scroll up (after 200px)
          if (y > 200) {
            if (y > lastY + 4) {
              header.classList.add('wa-header-hidden');
            } else if (y < lastY - 4) {
              header.classList.remove('wa-header-hidden');
            }
          } else {
            header.classList.remove('wa-header-hidden');
          }
          lastY = y;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  function boot() {
    initStagger();
    initReveal();
    initPageLoad();
    initHeaderScroll();
    // Slight delay on card depth — let layout settle first
    if (!REDUCED) {
      setTimeout(initCardDepth, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
